import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { trpc } from "@/lib/trpc";
import type { DialogInitial } from "./AgendaAppointmentDialog";

/**
 * `/agenda?reagendar=<id>` abre a criação de consulta já preenchida com o paciente, o profissional
 * e o procedimento da consulta indicada.
 *
 * Existe porque "Reagendar consulta" — a ação principal de uma consulta cancelada ou com falta —
 * precisava de um destino de verdade: mandar pra `/agenda` seca obrigaria a digitar tudo de novo.
 * O horário NÃO é copiado: reagendar é justamente escolher outro, e herdar o antigo daria a falsa
 * impressão de que já está resolvido.
 */
export function useReagendarPorUrl({
  pronto,
  horarioPadrao,
  aoAbrir,
}: {
  /** Só dispara com as configurações da agenda carregadas (horário/duração padrão). */
  pronto: boolean;
  horarioPadrao: { date: Date; startTime: string; endTime: string };
  aoAbrir: (initial: DialogInitial) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const id = new URLSearchParams(location.search).get("reagendar");
  const jaAbriu = useRef<string | null>(null);

  const consulta = trpc.appointment.getById.useQuery(
    { id: id ?? "" },
    { enabled: !!id && pronto },
  );

  useEffect(() => {
    if (!id || !pronto || !consulta.data || jaAbriu.current === id) return;
    jaAbriu.current = id;
    const a = consulta.data as any;
    aoAbrir({
      ...horarioPadrao,
      patientId: a.patientId,
      patientName: a.patientName,
      patientPhone: a.patientPhone ?? undefined,
      dentistId: a.dentistId,
      dentistName: a.dentistName ?? undefined,
      procedure: a.procedure,
      serviceIds: (a.services ?? []).map((s: any) => s.serviceId).filter(Boolean),
      color: a.color,
      status: "SCHEDULED",
    });
    // Limpa o parâmetro pra um F5 não reabrir o diálogo sozinho.
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pronto, consulta.data]);
}
